import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ethers } from 'ethers'
import { Memecoin } from '../../types/reducerTypes'
import { amountToWei } from '../../utils/amount'
import useWeb3 from '../../hooks/useWeb3'
import { useLazyGetContractByNameQuery } from '../../modules/web3/api'
import { setBalanceTokenSale } from '../../modules/web3/actions'
import useNetworkId from '../../hooks/useNetworkId'
import useIsLoggedIn from '../../hooks/useIsLoggedIn'
import useEthBalance from '../../hooks/useWeb3/useEthBalance'

export function bigIntWeiToEthNumber(wei: bigint, decimals = 18) {
  return Number(wei) * 10 ** -decimals
}

// this stops us cooking the RPC nodes since we don't need chain data
// this was actually the reason we chose Go (to store this data ourselves on the back end in real time so it scales better)
export function getCardLevelTokenValues(memecoin: Memecoin) {
  const ethRaised = memecoin?.meta?.ethRaised?.value ?? 0

  const isFourEthPool = memecoin?.isFourEthPool ?? false

  const totalEthToRaise = BigInt(
    amountToWei(isFourEthPool ? '4' : '5').toNumber(),
  )
  const totalEthToRaiseDecimal = bigIntWeiToEthNumber(totalEthToRaise)
  const ethRaisedDecimal = bigIntWeiToEthNumber(BigInt(ethRaised))
  const percentageProgress = (ethRaisedDecimal / totalEthToRaiseDecimal) * 100
  const isSaleComplete = percentageProgress >= 100

  return {
    ethRaisedDecimal,
    totalEthToRaiseDecimal,
    percentageProgress,
    isSaleComplete,
  }
}

function useTokenContractValues({ memecoin }: { memecoin: Memecoin }) {
  const ethRaised = memecoin?.meta?.ethRaised?.value

  const { address: walletAddress } = useIsLoggedIn()
  const [fallbackEthRaised, setFallbackEthRaised] = useState(BigInt(0))
  const [tokensSold, setTokensSold] = useState(BigInt(0))
  const [shouldPoll, setShouldPoll] = useState(false)
  const [userTokenBalance, setUserTokenBalance] = useState('0')

  const { updateEthBalance } = useEthBalance()

  const dispatch = useDispatch()

  const { poolAddress, tokenAddress } = memecoin

  const poolContractInstance = useGetReadOnlyContractInstance(
    'LiquidityFreeLaunch',
    poolAddress,
  )
  const erc20ContractInstance = useGetReadOnlyContractInstance(
    'DegenERC20',
    tokenAddress,
  )
  const poolContractEthersInstance = useGetEthersContractInstanceWithSigner(
    'LiquidityFreeLaunch',
    poolAddress,
  )
  const erc20ContractEthersInstance = useGetEthersContractInstanceWithSigner(
    'DegenERC20',
    tokenAddress,
  )

  const fetchSaleInformation = useCallback(async () => {
    try {
      if (!poolContractInstance || !erc20ContractInstance) return

      const [contractEthBalance, totalSold] = await Promise.all([
        poolContractInstance.getBalance(),
        poolContractInstance.getTotalSold(),
      ])

      setFallbackEthRaised(BigInt(contractEthBalance))
      setTokensSold(BigInt(totalSold))

      if (!walletAddress) return

      const userBalanceInSaleToken =
        await erc20ContractInstance.balanceOf(walletAddress)

      dispatch(setBalanceTokenSale(userBalanceInSaleToken))
      setUserTokenBalance(userBalanceInSaleToken)

      await updateEthBalance()

      setShouldPoll(true)
    } catch (error) {
      console.error('Error fetching token information', error)
    }
  }, [
    poolContractInstance,
    erc20ContractInstance,
    walletAddress,
    dispatch,
    updateEthBalance,
  ])

  useEffect(() => {
    if (!!Number(userTokenBalance)) return
    if (!!fallbackEthRaised) return
    if (!!tokensSold) return
    fetchSaleInformation()
  }, [fallbackEthRaised, fetchSaleInformation, tokensSold, userTokenBalance])

  useEffect(() => {
    if (!shouldPoll) return
    const interval = setInterval(fetchSaleInformation, 15000)
    return () => clearInterval(interval)
  }, [shouldPoll, fetchSaleInformation])

  const isFourEthPool = memecoin?.isFourEthPool ?? false

  const {
    ethRaisedDecimal,
    totalEthToRaiseDecimal,
    percentageProgress,
    isSaleComplete,
  } = useMemo(() => {
    const totalEthToRaise = BigInt(
      amountToWei(isFourEthPool ? '4' : '5').toNumber(),
    )
    const totalEthToRaiseDecimal = bigIntWeiToEthNumber(totalEthToRaise)
    const ethRaisedDecimal = bigIntWeiToEthNumber(
      BigInt(ethRaised || fallbackEthRaised),
    )
    const percentageProgress = (ethRaisedDecimal / totalEthToRaiseDecimal) * 100
    const isSaleComplete = percentageProgress >= 100

    return {
      ethRaisedDecimal,
      totalEthToRaiseDecimal,
      percentageProgress,
      isSaleComplete,
    }
  }, [ethRaised, fallbackEthRaised, isFourEthPool])

  return {
    ethRaisedDecimal,
    totalEthToRaiseDecimal,
    tokensSold,
    userTokenBalance,
    percentageProgress,
    isSaleComplete,
    decimals: 6, // hardcode for  now
    poolContractInstance,
    erc20ContractInstance,
    refetch: fetchSaleInformation,
    poolContractEthersInstance,
    erc20ContractEthersInstance,
  }
}

export default useTokenContractValues

export const BASE_NETWORK_IDS = [8453, 84532]

function useGetReadOnlyContractInstance(contractName: string, address: string) {
  const { getWriteOnlyContract } = useWeb3()
  const { networkId, wrongNetwork } = useNetworkId()

  const [getContract] = useLazyGetContractByNameQuery()
  const [contractInstance, setContractInstance] =
    useState<ethers.Contract | null>(null)

  const fetchContract = useCallback(async () => {
    try {
      if (!address) return // not really an error

      if (wrongNetwork) {
        console.info(`Error fetching contract due to wrong network`)
        return
      }

      const data = await getContract({
        name: contractName,
        chainId: networkId,
      }).unwrap()

      if (!data?.contract?.abi) new Error('No contract abi')

      const contract = getWriteOnlyContract(address, data.contract.abi)

      setContractInstance(contract)
    } catch (error) {
      console.error('Error fetching contract', error)
    }
  }, [
    address,
    wrongNetwork,
    getContract,
    contractName,
    networkId,
    getWriteOnlyContract,
  ])

  useEffect(() => {
    if (!!contractInstance) return
    fetchContract()
  }, [contractInstance, fetchContract])

  return contractInstance
}

export function useGetContractInstanceReadOnly({
  contract,
}: {
  contract:
  | undefined
  | {
    address?: string
    abi?: ethers.InterfaceAbi
    name?: string
    chainId?: string
  }
}) {
  const { getReadOnlyContract } = useWeb3()
  const { networkId } = useNetworkId()

  const [contractInstance, setContractInstance] =
    useState<ethers.Contract | null>(null)

  const { abi, address, name, chainId } = contract ?? {}
  const fetchContract = useCallback(async () => {
    try {
      if (networkId && chainId && String(networkId) !== String(chainId)) {
        console.info(
          `Wrong network fetching ${name}. Expected ${chainId} but got ${networkId}`,
        )
        return
      }
      if (!address) {
        console.info(`No contract address fetching ${name}`)
        return
      }
      if (!abi) return

      // const useWallet =
      //   !!walletAddress &&
      //   !!account &&
      //   account?.toLowerCase() === walletAddress?.toLowerCase()
      //
      // console.info(
      //   `Fetching ${name} contract using ${useWallet ? 'web3' : 'baseNetwork'}`,
      //   { walletAddress, account, useWallet },
      // )

      const contract = getReadOnlyContract(address, abi)

      setContractInstance(contract)
    } catch (error) {
      console.error('Error fetching contract', error)
    }
  }, [networkId, chainId, address, abi, getReadOnlyContract, name])

  useEffect(() => {
    if (!!contractInstance) return
    fetchContract()
  }, [contractInstance, fetchContract])

  return contractInstance
}

export function useGetEthersContractInstanceWithSigner(
  contractName: string,
  address: string,
) {
  const { getWriteOnlyContract } = useWeb3()
  const { networkId } = useNetworkId()
  const { address: walletAddress } = useIsLoggedIn()

  const [getContract] = useLazyGetContractByNameQuery()
  const [contractInstance, setContractInstance] =
    useState<ethers.Contract | null>(null)

  const fetchContract = useCallback(async () => {
    try {
      const data = await getContract({
        name: contractName,
        chainId: networkId,
      }).unwrap()
      if (!data?.contract?.abi) return
      const { abi } = data.contract

      const contract = getWriteOnlyContract(address, abi)
      setContractInstance(contract)
    } catch (error) {
      console.error('Error fetching contract', error)
    }
  }, [getContract, contractName, networkId, getWriteOnlyContract, address])

  useEffect(() => {
    if (!!walletAddress) return
    setContractInstance(null)
  }, [walletAddress])

  useEffect(() => {
    if (!!contractInstance) return
    fetchContract()
  }, [contractInstance, fetchContract])

  return contractInstance
}

export function useGetContractInstanceWriteOnly({
  contract,
}: {
  contract:
  | undefined
  | {
    address?: string
    abi?: ethers.InterfaceAbi
    name?: string
    chainId?: string
  }
}) {
  const { getWriteOnlyContract } = useWeb3()
  const { networkId } = useNetworkId()
  const { address: walletAddress } = useIsLoggedIn()

  const [contractInstance, setContractInstance] =
    useState<ethers.BaseContract | null>(null)

  const { abi, address, name, chainId } = contract ?? {}
  const fetchContract = useCallback(async () => {
    try {
      if (chainId && networkId && String(networkId) !== String(chainId)) {
        console.info(
          `Wrong network fetching ${name}. Expected ${chainId} but got ${networkId}`,
        )
        return
      }
      if (!address) return // not really an error
      if (!abi) return

      // const ethersContract = await createEthersContractWithSigner(
      //   address,
      //   abi,
      //   walletAddress,
      //   provider,
      // )
      const contract = getWriteOnlyContract(address, abi)
      setContractInstance(contract)
    } catch (error) {
      console.error('Error fetching contract', error)
    }
  }, [networkId, chainId, address, abi, getWriteOnlyContract, name])

  useEffect(() => {
    if (!!walletAddress) return
    setContractInstance(null)
  }, [walletAddress])

  useEffect(() => {
    if (!!contractInstance) return
    fetchContract()
  }, [contractInstance, fetchContract])

  return contractInstance
}

export function isBaseNetwork(networkId: number) {
  return BASE_NETWORK_IDS.includes(networkId)
}
